import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Eyebrow } from "./shared";
import { resolveUrl } from "@/lib/api";

const CHAPTERS = [
  { no: "01", title: "Visi", text: "Menjadi katalisator lahirnya juara dan profesional unggul yang menguasai logika eksakta." },
  { no: "02", title: "Metode", text: "Pembinaan berbasis problem-solving, terukur, dan dipandu pakar dengan rekam jejak nasional." },
  { no: "03", title: "Dampak", text: "Prestasi nyata bagi siswa dan lompatan kompetensi bagi organisasi mitra kami." },
];

export const About = ({ settings = {}, team = [] }) => {
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow testid="about-eyebrow">Tentang Kami</Eyebrow>
              <h2 className="mt-6 font-heading font-bold text-3xl md:text-5xl tracking-tight leading-tight">{settings.about_heading}</h2>
              <p className="mt-6 text-slate-400 leading-relaxed">{settings.about_paragraph}</p>
            </Reveal>
            <div className="mt-10 space-y-6">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.no} delay={i * 0.1}>
                  <div className="flex gap-5 group">
                    <span className="font-heading text-lg font-bold text-cyan/70 pt-1">{c.no}</span>
                    <div className="border-l border-white/10 pl-5 group-hover:border-gold transition-colors duration-300">
                      <h3 className="font-heading font-semibold text-lg">{c.title}</h3>
                      <p className="text-sm text-slate-400 mt-1 leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden rounded-3xl border border-white/10">
                <div className="absolute inset-0 radial-glow z-0" />
                {settings.about_image && (
                  <img src={resolveUrl(settings.about_image)} alt="Sesi pembinaan Binara Labs" className="relative z-10 w-full h-[280px] md:h-[380px] object-cover" />
                )}
                <div className="absolute bottom-0 inset-x-0 z-20 p-6 bg-gradient-to-t from-navy to-transparent">
                  <p className="font-heading text-sm text-slate-200">Ruang belajar tempat logika ditempa menjadi prestasi.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Meet The Team Section */}
        <div className="mt-24 md:mt-32">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <Eyebrow testid="team-eyebrow">Meet The Team</Eyebrow>
                <h2 className="mt-5 font-heading font-bold text-3xl md:text-4xl tracking-tight">Pakar di balik setiap prestasi</h2>
              </div>
              <p className="text-slate-400 max-w-sm text-sm">Klik pada anggota tim untuk melihat profil dan detail tugas secara lengkap.</p>
            </div>
          </Reveal>

          {/* Kartu Ringkas (Hanya menampilkan nama, jabatan singkat, dan foto dengan interaksi klik) */}
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <motion.div
                key={m.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -8 }}
                onClick={() => setSelectedMember(m)}
                data-testid={`team-card-${i}`}
                className="group cursor-pointer rounded-2xl border border-white/10 bg-navy-card/60 overflow-hidden hover:border-gold/40 hover:shadow-[0_10px_40px_rgba(0,240,255,0.08)] transition-all duration-300"
              >
                <div className="relative overflow-hidden">
                  <img src={resolveUrl(m.image_url)} alt={m.name} className="w-full h-56 object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                </div>
                <div className="p-5 text-center">
                  <h3 className="font-heading font-semibold text-lg text-white group-hover:text-cyan transition-colors">{m.name}</h3>
                  <p className="text-xs text-gold font-body font-semibold mt-1 uppercase tracking-wide truncate">{m.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* POPUP / MODAL DETAIL ANGGOTA TIM */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedMember(null)}
            className="fixed inset-0 z-[100] bg-navy/90 backdrop-blur-md grid place-items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-xl rounded-3xl border border-white/15 bg-navy-card p-6 md:p-8 shadow-2xl overflow-hidden"
            >
              <button
                onClick={() => setSelectedMember(null)}
                className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full border border-white/10 text-slate-300 hover:bg-white/10 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-2xl overflow-hidden border border-white/20 shrink-0 shadow-lg">
                  <img src={resolveUrl(selectedMember.image_url)} alt={selectedMember.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <span className="inline-block px-3 py-1 rounded-full bg-gold/15 text-gold text-xs font-bold uppercase tracking-wider mb-2">
                    {selectedMember.role}
                  </span>
                  <h3 className="font-heading font-bold text-2xl text-white">{selectedMember.name}</h3>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <h4 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-1">Deskripsi & Tugas Utama:</h4>
                    <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{selectedMember.desc}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};